'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { User, Building, Plus } from 'lucide-react';

interface InvestmentKanbanWidgetProps {
  autoPlay?: boolean;
  className?: string;
}

// Investment states matching the real app
type InvestmentState = 'prospect' | 'financing' | 'funded' | 'declared';

// Investor type matching the real app
type InvestorType = 'individual' | 'company';

interface MockInvestment {
  id: string;
  promoterName: string;
  investorName: string;
  investorType: InvestorType;
  amount: number;
  returnPercentage: number;
  state: InvestmentState;
  relativeTime: string;
}

// Column configuration matching the real kanban exactly
const columns: { id: InvestmentState; title: string; dotClass: string }[] = [
  { id: 'prospect', title: 'Prospecto', dotClass: 'bg-[#71717a]' },
  { id: 'financing', title: 'En financiacion', dotClass: 'bg-[#bfff00]' },
  { id: 'funded', title: 'Financiado', dotClass: 'bg-[#10b981]' },
  { id: 'declared', title: 'Comunicado', dotClass: 'bg-[#c89a5f]' },
];

// State badge configuration matching badgeHelpers.ts exactly
const stateBadgeConfig: Record<
  InvestmentState,
  { bg: string; text: string; dot: string; label: string }
> = {
  prospect: {
    bg: 'rgba(113, 113, 122, 0.1)',
    text: '#52525b',
    dot: '#71717a',
    label: 'Prospecto',
  },
  financing: {
    bg: 'rgba(191, 255, 0, 0.25)',
    text: '#6b8e00',
    dot: '#bfff00',
    label: 'En Financiacion',
  },
  funded: {
    bg: 'rgba(16, 185, 129, 0.1)',
    text: '#059669',
    dot: '#10b981',
    label: 'Financiado',
  },
  declared: {
    bg: 'rgba(200, 154, 95, 0.15)',
    text: '#c89a5f',
    dot: '#c89a5f',
    label: 'Comunicado',
  },
};

// Type badge configuration matching TypeBadge.tsx exactly
const typeBadgeConfig: Record<InvestorType, { bg: string; text: string; label: string }> = {
  individual: {
    bg: 'rgba(245, 158, 11, 0.1)',
    text: '#f59e0b',
    label: 'Autonomo',
  },
  company: {
    bg: 'rgba(59, 130, 246, 0.1)',
    text: '#3b82f6',
    label: 'Empresa',
  },
};

// Mock investments - realistic data matching the domain
const initialInvestments: MockInvestment[] = [
  // Prospecto column
  {
    id: 'INV-A1B2',
    promoterName: 'Aurora Films',
    investorName: 'Carlos Mendez',
    investorType: 'individual',
    amount: 25000,
    returnPercentage: 5.5,
    state: 'prospect',
    relativeTime: 'Hoy',
  },
  {
    id: 'INV-C3D4',
    promoterName: 'Velvet Music',
    investorName: 'Tech Ventures SL',
    investorType: 'company',
    amount: 50000,
    returnPercentage: 6.0,
    state: 'prospect',
    relativeTime: 'Ayer',
  },
  // En financiacion column - this is where the dragged card starts
  {
    id: 'INV-E5F6',
    promoterName: 'Stellar Productions',
    investorName: 'Maria Garcia',
    investorType: 'individual',
    amount: 35000,
    returnPercentage: 5.8,
    state: 'financing',
    relativeTime: 'Hace 2d',
  },
  {
    id: 'INV-G7H8',
    promoterName: 'Nova Cine',
    investorName: 'Iberica Capital',
    investorType: 'company',
    amount: 75000,
    returnPercentage: 6.2,
    state: 'financing',
    relativeTime: 'Hace 3d',
  },
  // Financiado column
  {
    id: 'INV-I9J0',
    promoterName: 'Lumina Studios',
    investorName: 'Pedro Ruiz',
    investorType: 'individual',
    amount: 45000,
    returnPercentage: 5.5,
    state: 'funded',
    relativeTime: 'Hace 5d',
  },
  {
    id: 'INV-K1L2',
    promoterName: 'Cascade Films',
    investorName: 'Global Invest SA',
    investorType: 'company',
    amount: 120000,
    returnPercentage: 6.5,
    state: 'funded',
    relativeTime: 'Hace 1sem',
  },
  // Comunicado column
  {
    id: 'INV-M3N4',
    promoterName: 'Horizon Media',
    investorName: 'Ana Torres',
    investorType: 'individual',
    amount: 30000,
    returnPercentage: 5.2,
    state: 'declared',
    relativeTime: 'Hace 2sem',
  },
  {
    id: 'INV-O5P6',
    promoterName: 'Summit Arts',
    investorName: 'Nexus Holdings',
    investorType: 'company',
    amount: 85000,
    returnPercentage: 6.0,
    state: 'declared',
    relativeTime: 'Hace 3sem',
  },
];

// The card that will be dragged
const DRAGGED_CARD_ID = 'INV-E5F6';

export function InvestmentKanbanWidget({
  autoPlay = true,
  className,
}: InvestmentKanbanWidgetProps) {
  const [investments, setInvestments] = useState<MockInvestment[]>(initialInvestments);
  const [draggedCardId, setDraggedCardId] = useState<string | null>(null);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [targetColumn, setTargetColumn] = useState<InvestmentState | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const animationRef = useRef<boolean>(true);
  const positionRef = useRef({ x: 0, y: 0 });

  // Detect mobile (< lg breakpoint = 1024px)
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!autoPlay) return;
    animationRef.current = true;

    const animateDrag = (targetX: number, targetY: number, duration: number) => {
      return new Promise<void>((resolve) => {
        const startX = positionRef.current.x;
        const startY = positionRef.current.y;
        const startTime = Date.now();

        const animate = () => {
          if (!animationRef.current) return resolve();

          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          // Smooth easing - cubic ease out
          const eased = 1 - Math.pow(1 - progress, 3);

          const newX = startX + (targetX - startX) * eased;
          const newY = startY + (targetY - startY) * eased;
          positionRef.current = { x: newX, y: newY };
          setDragPosition({ x: newX, y: newY });

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            resolve();
          }
        };

        requestAnimationFrame(animate);
      });
    };

    const runSequence = async () => {
      // 1. Reset to initial state
      setInvestments(initialInvestments);
      setDraggedCardId(null);
      setIsDragging(false);
      setTargetColumn(null);
      positionRef.current = { x: 0, y: 0 };
      setDragPosition({ x: 0, y: 0 });

      // 2. Pause to let the kanban be seen
      await delay(2500);
      if (!animationRef.current) return;

      // 3. Start drag - pick up the card
      setDraggedCardId(DRAGGED_CARD_ID);
      await delay(200);
      if (!animationRef.current) return;
      setIsDragging(true);

      // 4. Animate drag to funded column (move right)
      // Each column is 210px wide with gap-2.5 (10px)
      // For a card moving one column right, we need to move by column width + gap
      const columnWidth = 220; // 210px + 10px gap

      // Slight lift first
      await animateDrag(0, -8, 300);
      if (!animationRef.current) return;

      // Set target column highlight
      setTargetColumn('funded');

      // Move to funded column (one column to the right)
      await animateDrag(columnWidth, -12, 500);
      if (!animationRef.current) return;

      // Settle down
      await animateDrag(columnWidth, 0, 200);
      if (!animationRef.current) return;

      // 5. Update state to show card in new column with new badge
      setInvestments((prev) =>
        prev.map((inv) =>
          inv.id === DRAGGED_CARD_ID ? { ...inv, state: 'funded' as InvestmentState } : inv
        )
      );

      setIsDragging(false);
      setDraggedCardId(null);
      setTargetColumn(null);
      positionRef.current = { x: 0, y: 0 };
      setDragPosition({ x: 0, y: 0 });

      // 6. Pause before loop
      await delay(3000);
      if (!animationRef.current) return;

      // 7. Loop
      if (animationRef.current) runSequence();
    };

    runSequence();
    return () => {
      animationRef.current = false;
    };
  }, [autoPlay]);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getInvestmentsByState = (state: InvestmentState) => {
    return investments.filter((inv) => inv.state === state);
  };

  const renderCard = (investment: MockInvestment) => {
    const isBeingDragged = draggedCardId === investment.id && isDragging;
    const stateConfig = stateBadgeConfig[investment.state];
    const typeConfig = typeBadgeConfig[investment.investorType];

    return (
      <motion.div
        key={investment.id}
        layout
        initial={{ opacity: 0, y: 10 }}
        animate={{
          opacity: isBeingDragged ? 0.4 : 1,
          y: 0,
          scale: isBeingDragged ? 1.02 : 1,
          rotate: isBeingDragged ? 2 : 0,
        }}
        transition={{
          duration: 0.3,
          layout: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] },
        }}
        className={`
          bg-white border border-[#e4e4e7] rounded-lg p-2.5
          shadow-sm select-none
          ${isBeingDragged ? 'shadow-lg' : ''}
        `}
        style={{
          transformOrigin: 'center center',
        }}
      >
        {/* Row 1: Promoter Name */}
        <h4 className="text-[11px] font-semibold text-[#0a0a0a] truncate mb-0.5">
          {investment.promoterName}
        </h4>

        {/* Row 2: Investor Icon + Name */}
        <div className="flex items-center gap-1 mb-1.5">
          {investment.investorType === 'individual' ? (
            <User size={10} className="text-[#a1a1aa]" strokeWidth={2} />
          ) : (
            <Building size={10} className="text-[#a1a1aa]" strokeWidth={2} />
          )}
          <span className="text-[10px] text-[#71717a] truncate">{investment.investorName}</span>
        </div>

        {/* Row 3: Amount */}
        <div className="text-sm font-bold text-[#0a0a0a]">{formatCurrency(investment.amount)}</div>

        {/* Row 4: Return Percentage */}
        <div className="text-[10px] text-[#71717a] mb-1.5">
          Ret. {investment.returnPercentage.toFixed(1)}%
        </div>

        {/* Row 5: Badges */}
        <div className="flex items-center gap-1 flex-wrap mb-1.5">
          {/* Status Badge with bar variant */}
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold"
            style={{ backgroundColor: stateConfig.bg, color: stateConfig.text }}
          >
            <span
              className="w-0.5 h-2.5 rounded-full mr-1"
              style={{ backgroundColor: stateConfig.dot }}
            />
            {stateConfig.label}
          </span>

          {/* Type Badge */}
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium"
            style={{ backgroundColor: typeConfig.bg, color: typeConfig.text }}
          >
            {typeConfig.label}
          </span>
        </div>

        {/* Row 6: Footer */}
        <div className="flex items-center justify-between text-[9px] text-[#a1a1aa] pt-1.5 border-t border-[#f4f4f5]">
          <span className="font-mono">#{investment.id}</span>
          <span>{investment.relativeTime}</span>
        </div>
      </motion.div>
    );
  };

  // Render the floating dragged card
  const renderDraggedCard = () => {
    if (!draggedCardId || !isDragging) return null;

    const investment = investments.find((inv) => inv.id === draggedCardId);
    if (!investment) return null;

    // Show the card transitioning to funded state when over target column
    const displayState = targetColumn || investment.state;
    const stateConfig = stateBadgeConfig[displayState];
    const typeConfig = typeBadgeConfig[investment.investorType];

    return (
      <motion.div
        className="absolute z-50 pointer-events-none"
        initial={{ scale: 1, rotate: 0 }}
        animate={{
          scale: 1.05,
          rotate: 3,
          x: dragPosition.x,
          y: dragPosition.y,
        }}
        transition={{
          x: { duration: 0 },
          y: { duration: 0 },
          scale: { duration: 0.2 },
          rotate: { duration: 0.2 },
        }}
        style={{
          // Position at the original card location (financing column, first card)
          // Desktop: financing is index 1: column 0 width (210px) + gap (10px) + padding (8px) = 228px
          // Mobile: financing is index 0 (prospect hidden): just padding = 8px
          left: isMobile ? '8px' : '228px',
          top: '40px',
          width: '194px', // Card width within the 210px column (minus padding)
        }}
      >
        <div
          className="bg-white border border-[#e4e4e7] rounded-lg p-2.5"
          style={{
            boxShadow: '0 15px 40px rgba(0, 0, 0, 0.2), 0 5px 15px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Row 1: Promoter Name */}
          <h4 className="text-[11px] font-semibold text-[#0a0a0a] truncate mb-0.5">
            {investment.promoterName}
          </h4>

          {/* Row 2: Investor Icon + Name */}
          <div className="flex items-center gap-1 mb-1.5">
            {investment.investorType === 'individual' ? (
              <User size={10} className="text-[#a1a1aa]" strokeWidth={2} />
            ) : (
              <Building size={10} className="text-[#a1a1aa]" strokeWidth={2} />
            )}
            <span className="text-[10px] text-[#71717a] truncate">{investment.investorName}</span>
          </div>

          {/* Row 3: Amount */}
          <div className="text-sm font-bold text-[#0a0a0a]">
            {formatCurrency(investment.amount)}
          </div>

          {/* Row 4: Return Percentage */}
          <div className="text-[10px] text-[#71717a] mb-1.5">
            Ret. {investment.returnPercentage.toFixed(1)}%
          </div>

          {/* Row 5: Badges - animate badge change */}
          <div className="flex items-center gap-1 flex-wrap mb-1.5">
            <AnimatePresence mode="wait">
              <motion.span
                key={displayState}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold"
                style={{ backgroundColor: stateConfig.bg, color: stateConfig.text }}
              >
                <span
                  className="w-0.5 h-2.5 rounded-full mr-1"
                  style={{ backgroundColor: stateConfig.dot }}
                />
                {stateConfig.label}
              </motion.span>
            </AnimatePresence>

            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium"
              style={{ backgroundColor: typeConfig.bg, color: typeConfig.text }}
            >
              {typeConfig.label}
            </span>
          </div>

          {/* Row 6: Footer */}
          <div className="flex items-center justify-between text-[9px] text-[#a1a1aa] pt-1.5 border-t border-[#f4f4f5]">
            <span className="font-mono">#{investment.id}</span>
            <span>{investment.relativeTime}</span>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className={className}>
      {/* Kanban board with subtle perspective - constrained max width */}
      <div
        className="relative max-w-[920px] mx-auto"
        style={{
          perspective: '1200px',
        }}
      >
        <motion.div
          initial={{ opacity: 0, rotateX: 3 }}
          animate={{ opacity: 1, rotateX: 0 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
          style={{
            transformStyle: 'preserve-3d',
          }}
        >
          {/* Columns container - fixed width columns */}
          <div className="flex gap-2.5 justify-center">
            {columns.map((column) => {
              const columnInvestments = getInvestmentsByState(column.id);
              const isTargetColumn = targetColumn === column.id;
              // Calculate column total for the money sum
              const columnTotal = columnInvestments.reduce((sum, inv) => sum + inv.amount, 0);

              return (
                <div
                  key={column.id}
                  className={`w-[210px] flex-shrink-0 ${column.id === 'prospect' ? 'hidden lg:block' : ''}`}
                >
                  {/* Column Header */}
                  <motion.div
                    className="bg-[#f4f4f5] rounded-t-lg px-2.5 py-2 border-b border-[#e4e4e7]"
                    animate={{
                      backgroundColor: isTargetColumn ? 'rgba(108, 71, 255, 0.08)' : '#f4f4f5',
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex items-center justify-between">
                      {/* Left: Color + Title + Counter */}
                      <div className="flex items-center gap-1.5">
                        {/* Color bar indicator */}
                        <div className={`w-0.5 h-3 rounded-full ${column.dotClass}`} />

                        {/* Column title */}
                        <h3 className="text-[11px] font-semibold text-[#0a0a0a]">{column.title}</h3>

                        {/* Counter badge */}
                        <span className="bg-[#e4e4e7] text-[#52525b] text-[10px] font-medium min-w-[16px] h-[16px] flex items-center justify-center rounded">
                          {columnInvestments.length}
                        </span>
                      </div>

                      {/* Right: Add button */}
                      <button
                        className="p-0.5 hover:bg-[#e4e4e7] rounded transition-colors"
                        title="Agregar inversion"
                      >
                        <Plus size={12} className="text-[#71717a]" />
                      </button>
                    </div>

                    {/* Volume summary below title - aligned with title text */}
                    <div className="mt-1 ml-2.5 text-[10px] text-[#52525b] font-medium">
                      {formatCurrency(columnTotal)}
                    </div>
                  </motion.div>

                  {/* Column Body - fixed height to prevent layout shift during drag */}
                  <motion.div
                    className="bg-[#f4f4f5] rounded-b-lg p-2 space-y-2 h-[400px] overflow-hidden"
                    animate={{
                      backgroundColor: isTargetColumn ? 'rgba(108, 71, 255, 0.08)' : '#f4f4f5',
                      boxShadow: isTargetColumn
                        ? 'inset 0 0 0 2px rgba(108, 71, 255, 0.3)'
                        : 'none',
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    {columnInvestments.map((investment) => renderCard(investment))}
                  </motion.div>
                </div>
              );
            })}
          </div>

          {/* Floating dragged card */}
          {renderDraggedCard()}
        </motion.div>
      </div>
    </div>
  );
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export default InvestmentKanbanWidget;
